import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { getApiBaseUrl } from '../../services/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faUpload, faCopy, faCheck } from '@fortawesome/free-solid-svg-icons';
import './BulkUploadPanel.css';

interface BulkUploadPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  clientId: string;
}

interface QAItem {
  question: string;
  answer: string;
  category?: string;
}

interface Category {
  value: string;
  label: string;
}

const BulkUploadPanel: React.FC<BulkUploadPanelProps> = ({ isOpen, onClose, onSuccess, clientId }) => {
  const [jsonInput, setJsonInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [showExample, setShowExample] = useState(false);
  const [categories, setCategories] = useState<Category[]>([
    { value: 'general', label: 'General' }
  ]);

  // Fetch categories when panel opens
  useEffect(() => {
    if (isOpen && clientId) {
      fetchCategories();
    }
  }, [isOpen, clientId]);

  // Reset form when panel closes
  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setJsonInput('');
        setParseError(null);
      }, 300);
    }
  }, [isOpen]);

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const response = await fetch(`${getApiBaseUrl()}/client/${clientId}/prompt-categories`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.categories && data.categories.length > 0) {
          setCategories(data.categories);
        }
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const exampleJSON = `[
  {
    "question": "What are your business hours?",
    "answer": "We're open Monday-Friday, 9am-5pm EST.",
    "category": "${categories[0]?.value || 'general'}"
  },
  {
    "question": "Do you handle overpayment cases?",
    "answer": "Unfortunately, we do not handle Social Security overpayment cases. However, we recommend...",
    "category": "${categories.length > 1 ? categories[1].value : categories[0]?.value || 'general'}"
  }
]`;

  const validateJSON = (text: string): { valid: boolean; error?: string; data?: QAItem[] } => {
    if (!text.trim()) {
      return { valid: false, error: 'JSON input is empty' };
    }

    try {
      const parsed = JSON.parse(text);

      if (!Array.isArray(parsed)) {
        return { valid: false, error: 'JSON must be an array of Q&A objects' };
      }

      if (parsed.length === 0) {
        return { valid: false, error: 'Array is empty' };
      }

      if (parsed.length > 100) {
        return { valid: false, error: 'Maximum 100 items allowed per upload' };
      }

      for (let i = 0; i < parsed.length; i++) {
        const item = parsed[i];
        if (!item.question || typeof item.question !== 'string') {
          return { valid: false, error: `Item ${i + 1}: Missing or invalid 'question' field` };
        }
        if (!item.answer || typeof item.answer !== 'string') {
          return { valid: false, error: `Item ${i + 1}: Missing or invalid 'answer' field` };
        }
      }

      return { valid: true, data: parsed };
    } catch (error: any) {
      return { valid: false, error: `Invalid JSON: ${error.message}` };
    }
  };

  const handleInputChange = (text: string) => {
    setJsonInput(text);
    if (text.trim()) {
      const validation = validateJSON(text);
      setParseError(validation.valid ? null : validation.error || null);
    } else {
      setParseError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validation = validateJSON(jsonInput);
    if (!validation.valid) {
      toast.error(validation.error || 'Invalid JSON');
      setParseError(validation.error || null);
      return;
    }

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const response = await fetch(`${getApiBaseUrl()}/learned-qa/${clientId}/candidates/bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          items: validation.data,
          auto_approve: true  // Always auto-approve
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to upload Q&A data');
      }

      const data = await response.json();

      if (data.errors && data.errors.length > 0) {
        toast.warning(`Created ${data.created_count} items with ${data.errors.length} errors. Check console for details.`);
        console.error('Bulk upload errors:', data.errors);

        // Show detailed error messages
        data.errors.forEach((error: string) => {
          console.error('  -', error);
        });
      } else {
        toast.success(data.message || `Successfully created ${data.created_count} Q&A candidates`);
      }

      // Only close if at least some items were created
      if (data.created_count > 0) {
        onSuccess();
        onClose();
      }
    } catch (error: any) {
      console.error('Error bulk uploading Q&A:', error);
      toast.error(error.message || 'Failed to upload Q&A data');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopyExample = () => {
    navigator.clipboard.writeText(exampleJSON);
    toast.success('Example JSON copied to clipboard');
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="bulk-upload-panel-overlay" onClick={onClose} />
      <div className="bulk-upload-panel">
        {/* Header */}
        <div className="bulk-upload-panel-header">
          <div>
            <h2 className="bulk-upload-panel-title">Bulk Upload Training Data</h2>
            <p className="bulk-upload-panel-subtitle">
              Import multiple Q&A pairs at once using JSON format
            </p>
          </div>
          <button
            className="bulk-upload-panel-close"
            onClick={onClose}
            disabled={isSubmitting}
            title="Close"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        {/* Content */}
        <div className="bulk-upload-panel-content">
          <form onSubmit={handleSubmit} className="bulk-upload-form">
            {/* Example Section - Collapsible */}
            <div className="example-section">
              <button
                type="button"
                className="example-toggle"
                onClick={() => setShowExample(!showExample)}
              >
                <span>Example JSON Format:</span>
                <FontAwesomeIcon icon={showExample ? faXmark : faCopy} />
              </button>

              {showExample && (
                <>
                  <div className="example-actions">
                    <button
                      type="button"
                      className="copy-example-btn"
                      onClick={handleCopyExample}
                      disabled={isSubmitting}
                    >
                      <FontAwesomeIcon icon={faCopy} />
                      Copy Example
                    </button>
                  </div>
                  <pre className="example-json">{exampleJSON}</pre>
                  {categories.length > 0 && (
                    <div className="available-categories">
                      <strong>Available categories:</strong>{' '}
                      {categories.map((cat, idx) => {
                        const categoryDisplayNames: Record<string, string> = {
                          embeddings: 'knowledge',
                          knowledge_fallback: 'knowledge fallback',
                        };
                        return (
                          <span key={cat.value}>
                            <code className="category-badge">{categoryDisplayNames[cat.value] ?? cat.value.replace(/_/g, ' ')}</code>
                            {idx < categories.length - 1 ? ', ' : ''}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* JSON Input */}
            <div className="form-group">
              <textarea
                id="jsonInput"
                value={jsonInput}
                onChange={(e) => handleInputChange(e.target.value)}
                placeholder='Paste your JSON array here... Example: [{"question": "...", "answer": "..."}]'
                rows={14}
                required
                disabled={isSubmitting}
                className={`form-textarea code-textarea ${parseError ? 'error' : ''}`}
              />
              {parseError && (
                <div className="error-message">
                  <div className="error-icon">⚠️</div>
                  <div className="error-text">{parseError}</div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="form-actions">
              <button
                type="button"
                className="btn btn-cancel"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting || !!parseError}
              >
                <FontAwesomeIcon icon={isSubmitting ? faCheck : faUpload} />
                {isSubmitting ? 'Uploading...' : 'Upload Training Data'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default BulkUploadPanel;
